/* eslint-disable react/no-array-index-key */

// node modules
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'rc-tooltip';
import PulseLoader from 'react-spinners/PulseLoader';
import Menu, { MenuItem } from 'rc-menu';
import useStyles from 'isomorphic-style-loader/useStyles';

import { useSelector, useDispatch } from 'react-redux';
// MaterialUI
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckIcon from '@material-ui/icons/Check';

// app modules: actions
import removeItemFromCollection from 'actions/user/removeItemFromCollection';

// app modules: components
import ActionSheet from 'components/ActionSheet';
import AddToFavouritesItem from 'components/AddToFavouritesItem';
import BookRankOverlay from 'components/BookRankOverlay';
import Icon from 'components/Icon';
import ShareItem, { ShareMenuItems, ShareMenu } from 'components/ShareItem';
import ContentShareMenuItem from 'components/ShareItem/ContentShareMenuItem';
import AddToCollection from 'components/AddToCollection';
import AddToCollectionWrapper from 'components/AddToCollectionWrapper';
import PinRecipeItem from 'components/PinRecipeItem';

// app modules: props
import AuthorsProps from 'components/Authors/AuthorsProps';
import Image from 'components/Image';

// app modules: services
import assets from 'services/assets';
import route from 'services/route';
import history from 'services/history';
import isModerator from 'api/helpers/isModeratorHelper';

// app modules: constants
import { COLOURS, CONTENT_TYPES } from 'constants/index';

// assets
import CssContentOptions from './ContentOptions.scss';

const CollectionItemProps = {
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
};
const CollectionItemDefaultProps = {
  loading: false,
};
const CollectionListItem = ({ loading, children, ...others }) => (
  <ListItem className="content-options__item" button {...others}>
    {children}
    <PulseLoader loading={loading} color={COLOURS.ENERGIZED} sizeUnit="px" size={6} />
  </ListItem>
);
CollectionListItem.propTypes = CollectionItemProps;
CollectionListItem.defaultProps = CollectionItemDefaultProps;

const CollectionMenuItem = ({ loading, children, ...others }) => (
  <MenuItem {...others}>
    {children}
    <PulseLoader loading={loading} color={COLOURS.ENERGIZED} sizeUnit="px" size={6} />
  </MenuItem>
);
CollectionMenuItem.propTypes = CollectionItemProps;
CollectionMenuItem.defaultProps = CollectionItemDefaultProps;

const TextListItem = ({ children, ...others }) => (
  <ListItem className="content-options__item" {...others}>
    {children}
  </ListItem>
);
TextListItem.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

const AddToCollectionWrapperButtonMobile = ({ handleOpenClick, extraProps }) => (
  <TextListItem className="content-options__item" onClick={handleOpenClick} {...extraProps}>
    <ListItemIcon className="content-options__thumb">
      <Icon fill={COLOURS.ENERGIZED} name="list-add" height="30" width="30" />
    </ListItemIcon>
    <ListItemText primary="Add to collection" />
  </TextListItem>
);

AddToCollectionWrapperButtonMobile.propTypes = {
  handleOpenClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  extraProps: PropTypes.object,
};

AddToCollectionWrapperButtonMobile.defaultProps = {
  extraProps: {},
};

export default function ContentOptions({
                                         authors,
                                         book,
                                         buttonProps,
                                         page,
                                         children,
                                         className,
                                         collectionId,
                                         contentType,
                                         hash,
                                         heading,
                                         image,
                                         imageToShare,
                                         isbn,
                                         itemId,
                                         itemType,
                                         personId,
                                         showEmbed,
                                         subMenu,
                                         textToShare,
                                         urlToShare,
                                       }) {
  useStyles(CssContentOptions);

  const displayType = useSelector(state => state.dimensions.displayType);
  const loggedIn = useSelector(state => state.user.logged_in);
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();

  // Component state
  const [state, setState] = useState({
    isVisible: false,
    activeSubMenu: null,
    active: false,
    ModalComponent: null,
    currentSortingOption: { orderBy: 'created_at', orderByDirection: 'desc' },
    isSortActionSheetVisible: false,
    collectionFilter: '',
  });

  const isRecipe = useMemo(() => page && page.title && contentType === CONTENT_TYPES.RECIPE, [page]);

  // Component lifecycle
  useEffect(() => {
    let closeTimer = null;

    return () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    };
  }, []);

  const handleClose = navigateTo => {
    setState(prev => ({ ...prev, isVisible: false, activeSubMenu: null }));
    // Once the transition has finished become inactive - we don't need to render until we are used again
    setTimeout(() => {
      setState(prev => ({ ...prev, active: false }));
      if (navigateTo) {
        history.push(navigateTo);
      }
    }, 195);
  };

  const renderAuthors = (ItemComponent, extraProps) => {
    if (authors.length) {
      return authors.map(author => (
        <ItemComponent
          key={author.id}
          onClick={() => {
            handleClose(route.person(author.id, author.is_author));
          }}
          {...extraProps}
        >
          <ListItemAvatar className="content-options__thumb">
            <Avatar
              src={assets.avatarUrl(author.avatar_name, {
                resize: 'fit-in',
                width: 100,
                height: 100,
              })}
              alt={`${author.first_name} ${author.last_name}`}
            />
          </ListItemAvatar>
          <ListItemText>Go to {`${author.first_name} ${author.last_name}`}</ListItemText>
        </ItemComponent>
      ));
    }
    return null;
  };

  const renderBookLink = (ItemComponent, extraProps) => {
    if (book.isbn) {
      return (
        <ItemComponent
          key={book.isbn}
          onClick={() => {
            handleClose(route.book(book.isbn, book.signature_));
          }}
          {...extraProps}
        >
          <ListItemIcon className="content-options__thumb">
            <Image
              overlay={<BookRankOverlay ranking={book.ranking_recommendations} />}
              image={assets.coverUrl(book.image_name, {
                resize: 'fit-in',
                width: 100,
                height: 100,
              })}
              height={50}
              alt={book.title_short}
            />
          </ListItemIcon>
          <ListItemText>Go to book</ListItemText>
        </ItemComponent>
      );
    }
    return null;
  };

  // Function to remove an item from a collection
  const removeItemFromCollectionFn = async () => {
    await dispatch(removeItemFromCollection({ id: collectionId, itemId, itemType }));

    setState(prevState => ({ ...prevState, isVisible: false }));
  };

  const handleDialogOpen = ModalComponent => {
    handleClose();
    setState(prevState => ({ ...prevState, ModalComponent }));
  };

  const handleClick = () => {
    const { isVisible, isSortActionSheetVisible } = state;
    // For the sake of the transition, first set the active prop to allow the component to render.
    // Then follow with the visible flag which will start the transition.
    setState(prevState => ({ ...prevState, active: true }));

    if (isSortActionSheetVisible) {
      setState(prevState => ({ ...prevState, isSortActionSheetVisible: false }));
    } else {
      setTimeout(() => setState(prevState => ({ ...prevState, isVisible: !isVisible })), 0);

      setState(prevState => ({ ...prevState, isVisible: !isVisible }));
    }
  };

  const openSubMenu = _subMenu => {
    // Side out
    setState(prevState => ({ ...prevState, isVisible: false }));

    // Render collections menu (off-screen)
    setTimeout(() => {
      setState(prevState => ({ ...prevState, activeSubMenu: _subMenu, active: true }));
    }, 195);

    // Slide back in
    setTimeout(() => {
      setState(prevState => ({ ...prevState, isVisible: true }));
    }, 300);
  };

  const renderPopupMenuItems = () => {
    const extraProps = { popup: true, className: 'content-options__item' };
    const authorsMenuItem = renderAuthors(MenuItem, extraProps);
    const bookMenuItem = renderBookLink(MenuItem, extraProps);

    switch (subMenu) {
      case 'share':
        return (
          <ShareMenu
            popup
            showEmbed={showEmbed}
            className="content-options__item"
            iconClassName="content-options__thumb"
            ItemComponent={MenuItem}
            WrapperComponent={ContentShareMenuItem}
            contentId={itemId}
            textToShare={textToShare}
            urlToShare={urlToShare}
            shareImageUrl={imageToShare}
            onClick={handleClose}
            onComplete={handleClose}
            onDialogOpen={handleDialogOpen}
          />
        );

      default:
        return (
          <React.Fragment>
            <div className="content-options">
              <Menu selectable={false}>
                {loggedIn ? (
                  <MenuItem
                    className="content-options__item"
                    onClick={() => {
                      handleDialogOpen(AddToCollectionWrapper);
                    }}
                  >
                    <ListItemIcon className="content-options__thumb">
                      <Icon fill={COLOURS.ENERGIZED} name="list-add" height="30" width="30" />
                    </ListItemIcon>
                    <ListItemText>Add to collection</ListItemText>
                  </MenuItem>
                ) : null}
                {collectionId ? (
                  <CollectionMenuItem
                    className="content-options__item"
                    onClick={removeItemFromCollectionFn}
                  >
                    <ListItemIcon className="content-options__thumb">
                      <DeleteIcon fontSize="large" color="primary" />
                    </ListItemIcon>
                    <ListItemText>Remove from collection</ListItemText>
                  </CollectionMenuItem>
                ) : null}
                <AddToFavouritesItem
                  component={MenuItem}
                  contentType={contentType}
                  hash={hash}
                  className="content-options__item"
                  iconClassName="content-options__thumb"
                  itemId={itemId}
                  onClick={handleClose}
                />
                {isRecipe && (
                  <PinRecipeItem
                    className="content-options__item"
                    component={MenuItem}
                    contentType={contentType}
                    iconClassName="content-options__thumb"
                    itemId={itemId}
                    page={page}
                    onClick={handleClose}
                    {...extraProps}
                  />
                )}
                <ShareItem
                  showEmbed={showEmbed}
                  className="content-options__item"
                  iconClassName="content-options__thumb"
                  ItemComponent={MenuItem}
                  WrapperComponent={ContentShareMenuItem}
                  contentId={itemId}
                  textToShare={textToShare}
                  urlToShare={urlToShare}
                  shareImageUrl={imageToShare}
                  onClick={() => openSubMenu('share')}
                  onComplete={handleClose}
                  onDialogOpen={handleDialogOpen}
                  {...extraProps}
                />
                {bookMenuItem}
                {authorsMenuItem}
              </Menu>
            </div>
          </React.Fragment>
        );
    }
  };

  const renderActionSheetSort = (currentSortingOption, onSortOrderChange) => {
    const sortOptions = [
      { orderBy: 'name', orderByDirection: 'asc', title: 'Alphabetical' },
      { orderBy: 'available_from', orderByDirection: 'desc', title: 'Recently used' },
      { orderBy: 'created_at', orderByDirection: 'desc', title: 'Recently created' },
    ];

    return (
      <List disablePadding>
        {sortOptions.map(option => (
          <ListItem key={option.orderBy} onClick={() => onSortOrderChange(option)}>
            <ListItemText>{option.title}</ListItemText>
            {currentSortingOption.orderBy === option.orderBy && (
              <ListItemIcon>
                <CheckIcon fontSize="medium" color="primary" />
              </ListItemIcon>
            )}
          </ListItem>
        ))}
      </List>
    );
  };

  const SortingComponent = ({ currentSortingOption, onSortOrderChange }) => {
    const [showActionSheet, setShowActionSheet] = useState(false);

    const handleSortOptionChange = option => {
      onSortOrderChange(option);
      setShowActionSheet(false);
    };
    return (
      <React.Fragment>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowActionSheet(!showActionSheet)}
        >
          Sort
        </Button>
        {showActionSheet && (
          <ActionSheet
            withCloseButton
            itemRenderer={() => renderActionSheetSort(currentSortingOption, handleSortOptionChange)}
            swipeable={state.activeSubMenu !== 'collections' && subMenu !== 'collections'}
            visible
            onClose={() => setShowActionSheet(false)}
          />
        )}
      </React.Fragment>
    );
  };

  SortingComponent.propTypes = {
    currentSortingOption: PropTypes.shape({
      orderBy: PropTypes.string,
      orderByDirection: PropTypes.string,
    }).isRequired,
    onSortOrderChange: PropTypes.func.isRequired,
  };

  const renderActionSheetItems = () => {
    const extraProps = { button: true };
    const authorsMenuItem = renderAuthors(TextListItem, extraProps);
    const bookMenuItem = renderBookLink(TextListItem, extraProps);

    if (subMenu === 'collections' || state.activeSubMenu === 'collections') {
      return (
        <div className="content-options content-options__collections">
          <AddToCollection
            SortingComponent={SortingComponent}
            itemId={itemId}
            itemType={itemType}
          />
        </div>
      );
    } else if (subMenu === 'share' || state.activeSubMenu === 'share') {
      return (
        <div className="content-options">
          <List disablePadding>
            <ShareMenuItems
              showEmbed={showEmbed}
              className="content-options__item"
              ItemComponent={ListItem}
              WrapperComponent={ContentShareMenuItem}
              textToShare={textToShare}
              urlToShare={urlToShare}
              shareImageUrl={imageToShare}
              onClick={handleClose}
              onComplete={handleClose}
              onDialogOpen={handleDialogOpen}
            />
          </List>
        </div>
      );
    }

    return (
      <div className="content-options">
        <List disablePadding>
          {loggedIn ? (
            <TextListItem
              className="content-options__item"
              onClick={() => handleDialogOpen(AddToCollectionWrapper)}
              {...extraProps}
            >
              <ListItemIcon className="content-options__thumb">
                <Icon fill={COLOURS.ENERGIZED} name="list-add" height="30" width="30" />
              </ListItemIcon>
              <ListItemText primary="Add to collection" />
            </TextListItem>
          ) : null}
          {loggedIn && collectionId ? (
            <TextListItem
              className="content-options__item"
              onClick={removeItemFromCollectionFn}
              {...extraProps}
            >
              <ListItemIcon className="content-options__thumb">
                <DeleteIcon fontSize="large" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Remove from collection" />
            </TextListItem>
          ) : null}
          <AddToFavouritesItem
            className="content-options__item"
            component={TextListItem}
            contentType={contentType}
            hash={hash}
            iconClassName="content-options__thumb"
            itemId={itemId}
            onClick={handleClose}
            {...extraProps}
          />

          {isRecipe && (
            <PinRecipeItem
              className="content-options__item"
              component={TextListItem}
              contentType={contentType}
              iconClassName="content-options__thumb"
              itemId={itemId}
              page={page}
              onClick={handleClose}
              {...extraProps}
            />
          )}
          <ShareItem
            showEmbed={showEmbed}
            className="content-options__item"
            iconClassName="content-options__thumb"
            ItemComponent={ListItem}
            WrapperComponent={ContentShareMenuItem}
            textToShare={textToShare}
            urlToShare={urlToShare}
            shareImageUrl={imageToShare}
            onClick={() => openSubMenu('share')}
            onComplete={handleClose}
            onDialogOpen={handleDialogOpen}
            {...extraProps}
          />
          {bookMenuItem}
          {authorsMenuItem}
        </List>
      </div>
    );
  };

  const setModalComponent = component => {
    setState({ ...state, ModalComponent: component });
  };

  const button = (
    <Button color="primary" size="medium" variant="text" onClick={handleClick} {...buttonProps}>
      {children}
    </Button>
  );
  const modalElement = state.ModalComponent ? (
    <state.ModalComponent
      open
      itemId={itemId}
      itemType={itemType}
      isbn={isbn}
      personId={personId}
      contentTitle={heading}
      onClose={() => setModalComponent(null)}
    />
  ) : null;
  if (!state.active && !state.isVisible) {
    // Don't render items until the menu is visible, or about to open.
    return (
      <div className={className}>
        {modalElement}
        {button}
      </div>
    );
  }
  let subHeading;
  if (subMenu === 'collections' || state.activeSubMenu === 'collections') {
    subHeading = 'Add to collection';
  }

  const setIsVisible = visible => {
    setState({ ...state, isVisible: visible });
  };

  return (
    <div className={className}>
      {modalElement}
      {displayType === 'mobile' ? (
        <Fragment>
          {button}
          <ActionSheet
            withCloseButton
            swipeable={state.activeSubMenu !== 'collections' && subMenu !== 'collections'}
            itemRenderer={renderActionSheetItems}
            heading={state.activeSubMenu || subMenu ? null : heading}
            subHeading={subHeading}
            image={image}
            visible={state.isVisible}
            onClose={() => handleClose()}
          />
        </Fragment>
      ) : (
        <Tooltip
          defaultVisible={false}
          trigger={['click']}
          overlay={renderPopupMenuItems()}
          mouseEnterDelay={0.75}
          placement="bottom"
          onVisibleChange={visible => {
            setIsVisible(visible);
          }}
          overlayClassName="rc-tooltip--plain"
          visible={state.isVisible}
          destroyTooltipOnHide
          getTooltipContainer={
            () => document.querySelector('#search-bound-tooltip') || document.querySelector('body') // Add the tooltip to the search container if it is present so it scrolls with results.
          }
        >
          {button}
        </Tooltip>
      )}
    </div>
  );
}

// Define propTypes for the functional component
ContentOptions.propTypes = {
  authors: AuthorsProps.authors,
  book: PropTypes.shape({
    isbn: PropTypes.string,
    title: PropTypes.string,
    signature_: PropTypes.string,
  }),
  page: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    book: PropTypes.shape({
      image_name: PropTypes.string,
    }),
    primary_image: PropTypes.shape({
      crops: PropTypes.arrayOf(
        PropTypes.shape({
          sizes: PropTypes.arrayOf(
            PropTypes.shape({
              src: PropTypes.string,
            }),
          ),
        }),
      ),
    }),
  }),
  // eslint-disable-next-line react/forbid-prop-types
  buttonProps: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  collectionId: PropTypes.number,
  contentType: PropTypes.string,
  hash: PropTypes.string,
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  image: PropTypes.node,
  imageToShare: PropTypes.string,
  isbn: PropTypes.string,
  itemId: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
  loggedIn: PropTypes.bool,
  personId: PropTypes.string,
  removeItemFromCollection: PropTypes.func.isRequired,
  showEmbed: PropTypes.bool,
  subMenu: PropTypes.string,
  textToShare: PropTypes.string,
  urlToShare: PropTypes.string,
};

// Define defaultProps for the functional component
ContentOptions.defaultProps = {
  authors: [],
  book: {},
  buttonProps: {},
  page: {},
  className: '',
  collectionId: null,
  contentType: null,
  hash: '',
  heading: null,
  image: null,
  imageToShare: null,
  isbn: null,
  loggedIn: false,
  personId: null,
  showEmbed: true,
  subMenu: null,
  textToShare: null,
  urlToShare: null,
};
